import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, CheckCircle2, Circle, Trash2, Clock, AlertTriangle, Flag, ListTodo, Edit2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const PRIORITE = { urgent: { label: "Urgent", color: "bg-red-100 text-red-700", icon: AlertTriangle }, normal: { label: "Normal", color: "bg-blue-100 text-blue-700", icon: Flag }, bas: { label: "Bas", color: "bg-slate-100 text-slate-600", icon: Clock } };

const Taches = () => {
  const { token } = useAuth();
  const [taches, setTaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTache, setEditingTache] = useState(null);
  const [filter, setFilter] = useState("a_faire");
  const [form, setForm] = useState({ texte: "", priorite: "normal", client_nom: "", date_echeance: "" });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchTaches = async () => {
    try {
      const q = filter !== "all" ? `?statut=${filter}` : "";
      const res = await axios.get(`${API}/taches${q}`, { headers });
      setTaches(res.data);
    } catch { toast.error("Erreur chargement"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchTaches(); }, [filter]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      if (editingTache) {
        await axios.put(`${API}/taches/${editingTache.id}`, form, { headers });
        toast.success("Tâche modifiée !");
      } else {
        await axios.post(`${API}/taches`, form, { headers });
        toast.success("Tâche créée !");
      }
      setDialogOpen(false);
      setEditingTache(null);
      setForm({ texte: "", priorite: "normal", client_nom: "", date_echeance: "" });
      fetchTaches();
    } catch { toast.error("Erreur"); }
  };

  const openEdit = (t) => {
    setEditingTache(t);
    setForm({ texte: t.texte, priorite: t.priorite, client_nom: t.client_nom || "", date_echeance: t.date_echeance || "" });
    setDialogOpen(true);
  };

  const toggleStatut = async (t) => {
    const newStatut = t.statut === "a_faire" ? "fait" : "a_faire";
    try {
      await axios.put(`${API}/taches/${t.id}`, { statut: newStatut }, { headers });
      fetchTaches();
    } catch { toast.error("Erreur"); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/taches/${id}`, { headers });
      fetchTaches();
    } catch { toast.error("Erreur"); }
  };

  const aFaire = taches.filter(t => t.statut === "a_faire");
  const urgentes = aFaire.filter(t => t.priorite === "urgent");
  const faites = taches.filter(t => t.statut === "fait");

  const sorted = [...taches].sort((a, b) => {
    if (a.statut !== b.statut) return a.statut === "a_faire" ? -1 : 1;
    const pOrder = { urgent: 0, normal: 1, bas: 2 };
    return (pOrder[a.priorite] || 1) - (pOrder[b.priorite] || 1);
  });

  return (
    <div className="space-y-6 animate-fade-in" data-testid="taches-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Tâches</h1>
          <p className="text-slate-500 mt-1">Rappels et choses à faire</p>
        </div>
        <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={() => { setEditingTache(null); setForm({ texte: "", priorite: "normal", client_nom: "", date_echeance: "" }); setDialogOpen(true); }} data-testid="add-tache-btn">
          <Plus className="w-4 h-4 mr-2" /> Nouvelle tâche
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className={urgentes.length > 0 ? "border-red-300 bg-red-50" : ""}>
          <CardContent className="p-4">
            <p className="text-sm text-red-600">Urgentes</p>
            <p className="text-2xl font-bold text-red-700" data-testid="count-urgent">{urgentes.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">À faire</p>
            <p className="text-2xl font-bold text-slate-800" data-testid="count-a-faire">{aFaire.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Terminées</p>
            <p className="text-2xl font-bold text-emerald-600">{faites.length}</p>
          </CardContent>
        </Card>
      </div>

      <div className="flex gap-2">
        {[
          { key: "a_faire", label: "À faire" },
          { key: "fait", label: "Terminées" },
          { key: "all", label: "Toutes" },
        ].map(({ key, label }) => (
          <Button key={key} size="sm" variant={filter === key ? "default" : "outline"}
            className={filter === key ? "bg-indigo-600" : ""}
            onClick={() => setFilter(key)} data-testid={`filter-${key}`}>
            {label}
          </Button>
        ))}
      </div>

      {/* Task dialog */}
      <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) setEditingTache(null); }}>
        <DialogContent data-testid="tache-dialog">
          <DialogHeader><DialogTitle>{editingTache ? "Modifier la tâche" : "Nouvelle tâche"}</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label>Que faut-il faire ?</Label>
              <textarea value={form.texte} onChange={e => setForm({ ...form, texte: e.target.value })}
                className="w-full min-h-[80px] p-3 border rounded-md text-sm" placeholder="Ex: Contacter transporteur pour Alice Couffon..." required data-testid="tache-texte" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Priorité</Label>
                <Select value={form.priorite} onValueChange={v => setForm({ ...form, priorite: v })}>
                  <SelectTrigger data-testid="tache-priorite"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="urgent">Urgent</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="bas">Bas</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Échéance (optionnel)</Label>
                <Input type="date" value={form.date_echeance} onChange={e => setForm({ ...form, date_echeance: e.target.value })} data-testid="tache-echeance" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Client concerné (optionnel)</Label>
              <Input value={form.client_nom} onChange={e => setForm({ ...form, client_nom: e.target.value })} placeholder="Ex: Alice Couffon" data-testid="tache-client" />
            </div>
            <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="submit-tache-btn">{editingTache ? "Enregistrer" : "Créer"}</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Task list */}
      <Card data-testid="taches-list-card">
        <CardHeader><CardTitle className="text-lg flex items-center gap-2"><ListTodo className="w-5 h-5 text-indigo-600" />Tâches ({sorted.length})</CardTitle></CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div></div>
          ) : sorted.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <CheckCircle2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p className="font-medium">{filter === "a_faire" ? "Rien à faire !" : "Aucune tâche"}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {sorted.map(t => {
                const p = PRIORITE[t.priorite] || PRIORITE.normal;
                const isDone = t.statut === "fait";
                const PIcon = p.icon;
                return (
                  <div key={t.id} className={`flex items-start gap-3 p-3 rounded-lg border transition-all ${isDone ? "bg-slate-50 opacity-60" : "bg-white hover:bg-slate-50"}`} data-testid={`tache-${t.id}`}>
                    <button onClick={() => toggleStatut(t)} className="mt-0.5 shrink-0" data-testid={`toggle-${t.id}`}>
                      {isDone ? <CheckCircle2 className="w-5 h-5 text-emerald-500" /> : <Circle className="w-5 h-5 text-slate-300 hover:text-indigo-400" />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${isDone ? "line-through text-slate-400" : "text-slate-800"}`}>{t.texte}</p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <Badge className={`text-xs ${p.color}`}><PIcon className="w-3 h-3 mr-1" />{p.label}</Badge>
                        {t.client_nom && <Badge variant="outline" className="text-xs">{t.client_nom}</Badge>}
                        {t.date_echeance && <span className="text-xs text-slate-400">{t.date_echeance}</span>}
                      </div>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button size="sm" variant="ghost" className="text-slate-400 hover:text-indigo-500" onClick={() => openEdit(t)} data-testid={`edit-tache-${t.id}`}>
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-slate-400 hover:text-red-500" onClick={() => handleDelete(t.id)} data-testid={`delete-tache-${t.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Taches;
